import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import LazyImage from "@insite/mobius/LazyImage";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import React, { FC, useContext, useEffect, useState } from "react";
import { css } from "styled-components";
import Typography from "@insite/mobius/Typography";

const enum fields {
    background = "background",
    desktop_image = "desktop_image",
    tablet_image = "tablet_image",
    mobile_image = "mobile_image",
    backgroundColor = "backgroundColor",
}

interface Props extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.desktop_image]: string;
        [fields.tablet_image]: string;
        [fields.mobile_image]: string;
        [fields.backgroundColor]: string;
    };
}

export interface BrandDetailsContentStyles {
    container?: InjectableCss;
    imageWrapper: InjectableCss;
    linkStyles?: InjectableCss;
    brandName?: InjectableCss;
    description?: InjectableCss;
}

export const contentStyles: BrandDetailsContentStyles = {
    linkStyles: {
        css: css`
            margin-top: 8px;
            display: block;
            &:focus {
                outline-color: transparent !important;
            }
            > span {
                font-family: 'Roboto', sans-serif !important ;
                color: #262525 !important ;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: ${TYPOGRAPHY.size.large};
            }
        `,
    },
    imageWrapper: {
        css: css`

                aspect-ratio: 1/1;
    
                > div {
                    width: 100%;
                    height: 100%;
    
                    > img {
                        width: 100%;
                        height: auto;
                        object-fit: contain;
                        aspect-ratio: 1/1;
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: 235px;
                            max-height: 235px;
                            height: 235px;
                        `,
                        css`
                            max-width: 235px;
                            max-height: 235px;
                            height: 235px;
                        `,
                        css`
                            max-width: 235px;
                            max-height: 235px;
                            height: 235px;
                        `,
                        css`
                            max-width: 290px;
                            max-height: 290px;
                            height: 290px;
                        `,
                        css`
                            max-width: 290px;
                            max-height: 290px;
                            height: 290px;
                        `,
                    ])}
            `,
    },
    brandName: {
        css: css`
                font-family: 'Roboto Condensed', sans-serif;
                font-size: 40px !important;
                line-height: 48px !important;
                color: #2B5197;
                font-weight: 600 !important;
                margin: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 32px !important;
                        line-height: 40px !important;
                    `,
                    css`
                        font-size: 32px !important;
                        line-height: 40px !important;
                    `,
                    css`
                        font-size: 32px !important;
                        line-height: 40px !important;
                    `,
                    css`
                        font-size: 40px !important;
                        line-height: 48px !important;
                    `,
                    css`
                        font-size: 40px !important;
                        line-height: 48px !important;
                    `,
                ])}
            `,
    },
    description: {
        css: css`
                margin-top: 16px;   
                font-family: 'Roboto Condensed', sans-serif !important;
                color: #333333;
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: 18px !important;
                line-height: 28px !important;
            `,
    },
};

const styles = contentStyles;

const NISBrandDetailsContent: FC<Props> = ({ fields }) => {
    const { value: brand } = useContext(BrandStateContext);

    if (!brand ) {
        return null;
    }

    const backgroundDesktopStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.desktop_image).url}) !important;
               background-size: cover;
               max-width: 1800px;
               width: 100%;
               margin: 0 auto;
               background-repeat:no-repeat;
               `
            : `background-color: ${fields.backgroundColor};`;

    const backgroundTabletStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.tablet_image).url}) !important;
               background-size: cover;
               max-width: 1024px;
               width: 100%;
               margin: 0 auto;
               background-repeat:no-repeat;
               `
            : `background-color: ${fields.backgroundColor};`;

    const backgroundMobileStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.mobile_image).url}) !important;
               background-size: cover;
               max-width: 768px;
               width: 100%;
               margin: 0 auto;
               background-repeat:no-repeat;
               `
            : `background-color: ${fields.backgroundColor};`;


    const container = {
        css: css`
            ${backgroundDesktopStyles};
            display: flex;
            align-items: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        padding: 32px 16px;
                        gap: 16px;
                        ${backgroundMobileStyles}
                    `,
                    css`
                        flex-direction: column;
                        padding: 32px 16px;
                        gap: 16px;
                        ${backgroundMobileStyles}
                    `,
                    css`
                        padding: 40px 16px;
                        gap: 16px;
                        ${backgroundTabletStyles}
                    `,
                    css`
                        ${backgroundDesktopStyles};
                        padding: 24px 64px;
                        gap: 24px;
                    `,
                    css`
                        ${backgroundDesktopStyles};
                        padding: 24px 64px;
                        gap: 24px;
                    `,
                ])}
        `,
    };

    const [truncated, setTruncate] = useState(true);

    return (
        <StyledWrapper data-test-selector="brandContent" {...container}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={brand?.logoLargeImagePath} altText={brand?.name} />
            </StyledWrapper>

            <div>
                <Typography variant="h1" {...styles.brandName}>
                    {brand?.name}
                </Typography>
                <StyledWrapper {...styles.description}>
                    {!getIsWebCrawler() && truncated
                        ? parse(transformContentHtml(truncate(brand.htmlContent, { length: 450 })), parserOptions)
                        : parse(transformContentHtml(brand.htmlContent), parserOptions)}

                    {brand.htmlContent.length > 450 && !getIsWebCrawler() && (
                        <Link href="" onClick={() => setTruncate(!truncated)} {...styles.linkStyles}>
                            {truncated ? "Show More" : "Show Less"}
                        </Link>
                    )}
                </StyledWrapper>
            </div>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: NISBrandDetailsContent,
    definition: {
        group: "Brand Details",
        displayName: "NIS Content",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Contextual",
                name: fields.desktop_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Desktop_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.tablet_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Tablet_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.mobile_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Mobile_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            }
        ],
    },
};

export default widgetModule;
